/* eslint func-names: ["off"] */
import * as AWS from 'aws-sdk';
import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserPool,
} from 'amazon-cognito-identity-js';
import config from '../config';
import MerchantUser from './merchant-user';

/**
 * HaftahaveErrorResponse
 * @typedef {{
 *      error: boolean,
 *      message: string,
 *  }} HaftahaveErrorResponse
 */

/**
 * HaftahaveSuccessResponse
 * @typedef {{status:string} | Object<*>} HaftahaveSuccessResponse
 */

/**
 * @typedef {HaftahaveErrorResponse | HaftahaveSuccessResponse} HaftahaveResponse
 */

/**
 * CognitoError
 * @typedef {CognitoNetworkError|CognitoServiceError|CognitoUnknownError} CognitoError
 */

/**
 * CognitoNetworkError
 * @typedef {{
 *      code: string,
 *      name: string,
 *      message: string,
 * }} CognitoNetworkError
 */

/**
 * CognitoServiceError
 * @typedef {{
 *      code: string,
 *      name: string,
 *      statusCode: number,
 *      message: string,
 * }} CognitoServiceError
 */

/**
 * CognitoUnknownError
 * @typedef {{
 *      code: string,
 *      message: string,
 * }} CognitoUnknownError
 */

const authenticator = `cognito-idp.${config.cognito.region}.amazonaws.com/${config.cognito.userPoolId}`;

export const ALLOW_UNCONFIRMED_USERS = true;
export const ERROR_CODE_NOT_AUTHORIZED = 'NotAuthorizedException';
export const ERROR_CODE_USER_NOT_FOUND = 'UserNotFoundException';
export const ERROR_CODE_USER_UNCONFIRMED = 'UserNotConfirmedException';
export const ERROR_CODE_INVALID_PARAM = 'InvalidParameterException';
export const ERROR_CODE_INVALID_PASSWORD = 'InvalidPasswordException';
export const ERROR_CODE_LIMIT_EXCEEDED = 'LimitExceededException';
export const CHALLENGE_NAME_SMS_MFA = 'SMS_MFA';

/**
 * @param {string} userToken
 * @returns {Promise<void>}
 */
function setUserTokenToAwsCredentialsAndRefresh(userToken) {
    AWS.config.update({ region: config.cognito.region });

    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: config.cognito.identityPoolId,
        Logins: {
            [authenticator]: userToken,
        },
    });

    return AWS.config.credentials.getPromise();
}

/**
 * Library level function
 * Knows how to interact with cognito objects
 * @param {string} email
 * @param {string} password
 * @param {Object} authConfig
 * @returns void
 */
function awsLogin(email, password, authConfig = {}) {
    const userPool = new CognitoUserPool({
        UserPoolId: config.cognito.userPoolId,
        ClientId: config.cognito.appClientId,
    });
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authenticationData = { Username: email, Password: password };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const callbacks = {
        onSuccess: async (/** CognitoUserSession */session, userConfirmationNecessary) => {
            try {
                await setUserTokenToAwsCredentialsAndRefresh(session.getIdToken().getJwtToken());
            } catch (error) {
                console.error('Auth: Error storing token', error);
            }
            authConfig.onSuccess && authConfig.onSuccess(session, userConfirmationNecessary, user);
        },
        onFailure: (/** CognitoError */error) => {
            authConfig.onFailure && authConfig.onFailure(error, user);
        },
        mfaRequired: (challengeName, challengeParameters) => {
            authConfig.mfaRequired && authConfig.mfaRequired(challengeName, challengeParameters, user);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
            authConfig.newPasswordRequired && authConfig.newPasswordRequired(userAttributes, requiredAttributes, user);
        },
        totpRequired: (challengeName, challengeParameters) => {
            authConfig.totpRequired && authConfig.totpRequired(challengeName, challengeParameters, user);
        },
        customChallenge: (challengeParameters) => {
            authConfig.customChallenge && authConfig.customChallenge(challengeParameters, user);
        },
        mfaSetup: (challengeName, challengeParameters) => {
            authConfig.mfaSetup && authConfig.mfaSetup(challengeName, challengeParameters, user);
        },
        selectMFAType: (challengeName, challengeParameters) => {
            authConfig.selectMFAType && authConfig.selectMFAType(challengeName, challengeParameters, user);
        },
    };

    user.authenticateUser(authenticationDetails, callbacks);
}

/**
 * @param {string} email
 * @param {Object} authConfig
 * @returns void
 */
function awsForgotPassword(email, authConfig = {}) {
    const userPool = new CognitoUserPool({
        UserPoolId: config.cognito.userPoolId,
        ClientId: config.cognito.appClientId,
    });
    const user = new CognitoUser({ Username: email, Pool: userPool });

    const callbacks = {
        onSuccess(result) {
            authConfig.onSuccess && authConfig.onSuccess(result, user);
        },
        onFailure(error) {
            console.error(error);
            authConfig.onFailure && authConfig.onFailure(error, user);
        },
        inputVerificationCode(_data) {
            authConfig.inputVerificationCode && authConfig.inputVerificationCode(_data, user);
        },
    };

    user.forgotPassword(callbacks);
}

/**
 * @param {string} email
 * @param {string|number} verificationCode
 * @param {string} newPassword
 * @param {Object} authConfig
 * @returns void
 */
function awsForgotPasswordConfirm(email, verificationCode, newPassword, authConfig) {
    const userPool = new CognitoUserPool({
        UserPoolId: config.cognito.userPoolId,
        ClientId: config.cognito.appClientId,
    });
    const user = new CognitoUser({ Username: email, Pool: userPool });

    const callbacks = {
        onSuccess(result) {
            authConfig.onSuccess && authConfig.onSuccess(result, user);
        },
        onFailure(error) {
            console.error(error);
            authConfig.onFailure && authConfig.onFailure(error, user);
        },
    };

    user.confirmPassword(verificationCode, newPassword, callbacks);
}

/**
 * Get current user from local storage
 * @returns {CognitoUser|null}
 */
function getCurrentUser() {
    const userPool = new CognitoUserPool({
        UserPoolId: config.cognito.userPoolId,
        ClientId: config.cognito.appClientId,
    });
    return userPool.getCurrentUser();
}

/**
 * @param {Object} user
 * @returns {Promise<CognitoUserSession>}
 */
function getUserSession(user) {
    // user.getSession().promise();
    return new Promise((resolve, reject) => {
        user.getSession((error, session) => {
            if (error) {
                reject(error);
                return;
            }
            resolve(session);
        });
    });
}

/**
 * @returns {boolean}
 */
function isUpToDateAwsCredentials() {
    return AWS.config.credentials !== null && Date.now() < AWS.config.credentials.expireTime - config.cognito.auth.ttl;
}

/**
 * @returns void
 */
export function signOutUser() {
    const currentUser = getCurrentUser();
    if (currentUser !== null) {
        currentUser.signOut();
    }

    if (AWS.config.credentials) {
        AWS.config.credentials.clearCachedId();
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({});
    }
}

/**
 * @returns {Promise<boolean>}
 */
async function refreshCurrentUserTokens() {
    const currentUser = getCurrentUser();
    if (currentUser === null) {
        return false;
    }

    try {
        const userSession = await getUserSession(currentUser);
        const userToken = userSession.getIdToken().getJwtToken();
        await setUserTokenToAwsCredentialsAndRefresh(userToken);
        return true;
    } catch (e) {
        console.error(`Auth: Failed to update AWS credentials: ${e.message}`);
        signOutUser();
        return false;
    }
}

/**
 * @returns {Promise<{accessKey: string, secretKey: string, sessionToken: string}>}
 */
export async function getRequestCredentials() {
    if (!isUpToDateAwsCredentials()) {
        const authResult = await refreshCurrentUserTokens();
        if (!authResult) {
            throw new Error('Auth: Can\'t get request credentials, user is not authenticated');
        }
    }

    const { accessKeyId, secretAccessKey, sessionToken } = AWS.config.credentials;

    return {
        accessKey: accessKeyId,
        secretKey: secretAccessKey,
        sessionToken,
    };
}

/**
 * Framework level function
 * Knows how to handle expected errors
 * @param {string} email
 * @param {string} password
 * @param {Function} displayError
 * @param {Function} onAuthSuccess
 * @param {Function} onUnconfirmedLoginSuccess
 * @param {Function} onMfaRequired
 * @param {Function} onNewPasswordRequired
 * @returns {Promise<void>}
 */
export function haftahaveLogin(
    email,
    password,
    displayError,
    onAuthSuccess,
    onUnconfirmedLoginSuccess = null,
    onMfaRequired = null,
    onNewPasswordRequired = null,
) {
    return new Promise((resolve, reject) => {
        const authConfig = {
            onSuccess: async (/** CognitoUserSession */session, userConfirmationNecessary, /** CognitoUser */user) => {
                // NOTE: error thrown in this callback cause onFailure() callback invocation with "Unknown error"
                if (!onAuthSuccess || !(onAuthSuccess instanceof Function)) {
                    const message = 'Auth: onAuthSuccess() is not configured';
                    console.error(message);
                    displayError('Internal error in auth lib(1)');
                    reject(new Error(message));
                } else {
                    const merchantUser = new MerchantUser(user, session, true);
                    await onAuthSuccess(merchantUser);
                    resolve();
                }
            },
            onFailure: async (/** CognitoError */ error, /** CognitoUser */user) => {
                // NOTE: error param here is not an instance of Error
                // NOTE: errors thrown inside this callbacks will not be seen outside

                if (!error.code) {
                    console.error('Auth: Cognito error:', error.message || 'Unknown error (x)', error);
                    reject(new Error(error.message));
                }

                switch (error.code) {
                    case ERROR_CODE_NOT_AUTHORIZED:
                    case ERROR_CODE_USER_NOT_FOUND:
                        displayError(error.message);
                        reject(error.message);
                        break;

                    case ERROR_CODE_USER_UNCONFIRMED:
                        if (ALLOW_UNCONFIRMED_USERS) {
                            console.log('Unconfirmed user login successful');
                            if (onUnconfirmedLoginSuccess === null) {
                                const message = 'Auth: onUnconfirmedLoginSuccess() is not configured';
                                console.error(message);
                                displayError('Internal error in auth lib(2)');
                                reject(new Error(message));
                            } else {
                                const merchantUser = new MerchantUser(user, null, false);
                                await onUnconfirmedLoginSuccess(merchantUser);
                                resolve();
                            }
                        } else {
                            displayError(error.message);
                            reject(error.message);
                        }
                        break;

                    default:
                        console.error(`Auth: Cognito error: ${error.message}`, error);
                        reject(new Error(`Auth: Cognito error: ${error.message}`));
                        break;
                }
            },
            mfaRequired: async (challengeName, challengeParameters, /** CognitoUser */user) => {
                if (!onMfaRequired || !(onMfaRequired instanceof Function)) {
                    const message = 'Auth: onMfaRequired() is not configured';
                    console.error(message);
                    displayError('Internal error in auth lib(5)');
                    reject(new Error(message));
                } else {
                    if (challengeName !== CHALLENGE_NAME_SMS_MFA) {
                        const message = `Auth: Unknown type of MFA challenge "${challengeName}"`;
                        displayError('Internal error in auth lib(6)');
                        console.error(message);
                        reject(new Error(message));
                    }

                    // so we pass CognitoUser, but may be it's better to pass MerchantUser
                    // or pass nothing at all since we can get current user from CognitoUserPool
                    await onMfaRequired(user, challengeName, challengeParameters);
                    resolve();
                }
            },
            newPasswordRequired: async (userAttributes, requiredAttributes, /** CognitoUser */user) => {
                if (!onNewPasswordRequired || !(onNewPasswordRequired instanceof Function)) {
                    const message = 'Auth: onNewPasswordRequired() is not configured';
                    console.error(message);
                    displayError('Internal error in auth lib(3)');
                    reject(new Error(message));
                } else {
                    await onNewPasswordRequired(user, userAttributes, requiredAttributes);
                    resolve();
                }

                // User was signed up by an admin and must provide new
                // password and required attributes, if any, to complete
                // authentication.
            },
            customChallenge: () => {
                console.error('Auth: "customChallenge" mode is not implemented');
                displayError('Internal error in auth lib(4)');
                reject(new Error('Auth: "customChallenge" mode is not implemented'));
            },
        };

        awsLogin(email, password, authConfig);
    });
}

/**
 * @param {string} mfaCode
 * @param {Function} displayError
 * @param {Function} onMfaSuccess
 * @param {CognitoUser} user
 * @returns {Promise<void>}
 */
export async function haftahaveLoginMfa(mfaCode, displayError, onMfaSuccess, user = null) {
    const currentUser = user || getCurrentUser();

    return new Promise(function (resolve, reject) {
        currentUser.sendMFACode(
            mfaCode,
            {
                onSuccess: (signInUserSession) => {
                    if (!onMfaSuccess || !(onMfaSuccess instanceof Function)) {
                        const message = 'Auth: onMfaSuccess() is not configured';
                        console.error(message);
                        displayError('Internal error in auth lib(7)');
                        reject(new Error(message));
                    }
                    const merchantUser = new MerchantUser(currentUser, signInUserSession, true);
                    this.props.onMfaSuccess(merchantUser);
                    resolve();
                },
                onFailure: async (error) => {
                    // probably error here is not instance of Error
                    console.error(`Auth: Cognito error: ${error.message}`, error);
                    displayError(error.message);
                    reject(new Error(`Auth: Cognito error: ${error.message}`));
                },
            },
            CHALLENGE_NAME_SMS_MFA,
        );
    });
}

/**
 * @returns {Promise<MerchantUser|null>}
 */
export async function tryToAuthenticateWithStoredTokens() {
    let cognitoUser = null;
    let cognitoUserSession = null;
    if (isUpToDateAwsCredentials()) {
        cognitoUser = getCurrentUser();
        cognitoUserSession = await getUserSession(cognitoUser);
    } else {
        const authResult = await refreshCurrentUserTokens();
        if (authResult) {
            cognitoUser = getCurrentUser();
            cognitoUserSession = await getUserSession(cognitoUser);
        } else {
            // auth error: User is not logged in
            // It's ok, no data to login user
        }
    }

    const hasValidCredentials = cognitoUser !== null || cognitoUserSession !== null;
    const merchantUser = hasValidCredentials ? new MerchantUser(cognitoUser, cognitoUserSession, true) : null;

    return merchantUser;
}

/**
 * @param {string} email
 * @param {string} password
 * @param {string} newPassword
 * @param {Function} displayError
 * @param {Function} onAuthSuccess
 * @returns {Promise<void>}
 */
export async function haftahaveChangePassword(
    email,
    password,
    newPassword,
    displayError,
    onAuthSuccess,
) {
    return new Promise((resolve, reject) => {
        const authConfig = {
            onSuccess: async (/** CognitoUserSession */session, userConfirmationNecessary, /** CognitoUser */user) => {
                user.changePassword(password, newPassword, async (error, _result) => {
                    if (error) {
                        if (!error.code) {
                            console.error('Auth: Cognito error:', error.message || 'Unknown error (x)', error);
                            reject(new Error(error.message));
                        }

                        switch (error.code) {
                            case ERROR_CODE_INVALID_PARAM:
                            case ERROR_CODE_INVALID_PASSWORD:
                            case ERROR_CODE_LIMIT_EXCEEDED:
                                displayError(error.message);
                                reject(error.message);
                                break;

                            default:
                                console.error(`Auth: Cognito error: ${error.message}`, error);
                                reject(new Error(`Auth: Cognito error: ${error.message}`));
                                break;
                        }
                    } else if (!onAuthSuccess || !(onAuthSuccess instanceof Function)) {
                        const message = 'Auth: onAuthSuccess() is not configured';
                        console.error(message);
                        displayError('Internal error in auth lib(8)');
                        reject(new Error(message));
                    } else {
                        const merchantUser = new MerchantUser(user, session, true);
                        await onAuthSuccess(merchantUser);
                        resolve();
                    }
                });
            },
            onFailure: async (/** CognitoError */ error, /** CognitoUser */ _user) => {
                // NOTE: error param here is not an instance of Error
                // NOTE: errors thrown inside this callbacks will not be seen outside

                if (!error.code) {
                    console.error('Auth: Cognito error:', error.message || 'Unknown error (x)', error);
                    reject(new Error(error.message));
                }

                switch (error.code) {
                    case ERROR_CODE_NOT_AUTHORIZED:
                    case ERROR_CODE_USER_NOT_FOUND:
                    case ERROR_CODE_USER_UNCONFIRMED:
                        displayError(error.message);
                        reject(error.message);
                        break;

                    default:
                        console.error(`Auth: Cognito error: ${error.message}`, error);
                        reject(new Error(`Auth: Cognito error: ${error.message}`));
                        break;
                }
            },
            newPasswordRequired: (userAttributes, requiredAttributes, /** CognitoUser */user) => {
                // User was signed up by an admin and must provide new
                // password and required attributes, if any, to complete
                // authentication.

                // the api doesn't accept this field back
                const {
                    email_verified, // eslint-disable-line
                    phone_number_verified, // eslint-disable-line
                    ...otherUserAttributes
                } = userAttributes;

                // Get these details and call
                user.completeNewPasswordChallenge(newPassword, otherUserAttributes, {
                    onSuccess: async (signInUserSession) => {
                        if (!onAuthSuccess || !(onAuthSuccess instanceof Function)) {
                            const message = 'Auth: onAuthSuccess() is not configured';
                            console.error(message);
                            displayError('Internal error in auth lib(9)');
                            reject(new Error(message));
                        } else {
                            const merchantUser = new MerchantUser(user, signInUserSession, true);
                            await onAuthSuccess(merchantUser);
                            resolve();
                        }
                    },
                    onFailure: (error) => {
                        console.error(`Auth: Cognito error: ${error.message}`, error);
                        reject(new Error(`Auth: Cognito error: ${error.message}`));
                    },

                });
            },
        };

        awsLogin(email, password, authConfig);
    });
}

/**
 * @param {string} email
 * @param {Function} displayError
 * @returns {Promise<void>}
 */
export async function haftahaveRecoverPassword(email, displayError) {
    return new Promise((resolve, reject) => {
        const authConfig = {
            onSuccess(_result) {
                resolve();
            },
            onFailure(/** CognitoError */error) {
                // NOTE: error param here is not an instance of Error
                // NOTE: errors thrown inside this callbacks will not be seen outside

                if (!error.code) {
                    console.error('Auth: Cognito error:', error.message || 'Unknown error (x)', error);
                    reject(new Error(error.message));
                }

                switch (error.code) {
                    case ERROR_CODE_INVALID_PARAM:
                        displayError(error.message);
                        reject(error.message);
                        break;

                    default:
                        console.error(`Auth: Cognito error: ${error.message}`, error);
                        reject(new Error(`Auth: Cognito error: ${error.message}`));
                        break;
                }
            },
            inputVerificationCode(_data, _user) {
                resolve();
            },
        };

        awsForgotPassword(email, authConfig);
    });
}

/**
 * @param {string} email
 * @param {string|number} verificationCode
 * @param {string} newPassword
 * @returns {Promise<void>}
 * @throws
 */
export async function haftahaveRecoverPasswordConfirm(email, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
        const authConfig = {
            onSuccess(_result) {
                resolve();
            },
            onFailure(error) {
                console.error(error);
                reject();
            },
        };

        awsForgotPasswordConfirm(email, verificationCode, newPassword, authConfig);
    });
}
